import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Checkbox, FullPageModalAdditionalActions, FullPageModalHeaderStart, Grid, ConditionalWrapper, Tooltip, Typography, useDidUpdate, useIsMounted, WithInfoIcon, Separator, useDevice, FullPageModalMainActions } from 'seagull';
import { Eye } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { isAutoApprovalValid } from '~/business-logic/autoApprovalLogic';
import { hasYearlyAutomaticBillingError } from '~/business-logic/billingLogic';
import IntroVideoField from '~/components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoField';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { businessSettingsDefaults } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSignal } from '~/hooks/useSignal';
import { useLocaleKeys } from '~/i18n';
import { approvalMethodNoticePeriodModel, legalTermsModel, serviceModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { customPagesEnabled, isCustomPageContentInvalid } from '~/models/custom-page/model';
import { makeExpirationDate } from '~/models/expiration-date';
import { makeIntroVideoNone } from '~/models/intro-video/makers';
import { RelationshipStatus } from '~/models/relationship';
import { RequestAccountantAccess, requestAccountantAccessModel } from '~/models/request-accountant-access';
import { useValidateService } from '~/models/service/hooks/useValidateService';
import { toDateStampObject } from '~/models/time';
import { useIoContext } from '~/providers/IoContext';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { AllowManualPaymentsCheckbox, ApprovalMethodNoticePeriodField, CoverCreditCardFeesSelector } from '../../agreement-controllers';
import NetTermsDropdown from '../../NetTermsDropdown/NetTermsDropdown';
import styles from '../AgreementStudioPage.module.scss';
import { defaultCoverLetterNewClient } from '../AgreementStudioPage.utils';
import { resolveService, resolveVariablesInDelta, resolveVariablesInText } from '../TemplateVariables/TemplateVariables.utils';
import { TemplateVariablesProvider, useTemplateVariables } from '../TemplateVariables/TemplateVariablesContext';
import { TemplateVariablesSection } from '../TemplateVariables/TemplateVariablesSection/TemplateVariablesSection';
import { useStudioUiState } from '../useStudioUiState';
import { CustomPagesSection } from './CustomPagesSection/CustomPagesSection';
import ExpirationDate from './ExpirationDate/ExpirationDate';
import ExtraClause from './ExtraClause/ExtraClause';
import FormlingContainer from './FormlingContainer/FormlingContainer';
import IntroMessage, { validateIntroMessage } from './IntroMessage/IntroMessage';
import { LegalTerms } from './LegalTerms/LegalTerms';
import ServicePackages from './ServicePackages/ServicePackages';
import { useSendGate } from './useSendGate';
const InnerSep = (props)=>/*#__PURE__*/ _jsx("div", {
        className: cn(styles.innerSeparator, {
            [styles.thin]: props.thin
        })
    });
export var SubmitType = /*#__PURE__*/ function(SubmitType) {
    SubmitType["SendSilently"] = "sendSilently";
    SubmitType["SendProposal"] = "sendProposal";
    SubmitType["ExternallySign"] = "externallySign";
    SubmitType["ScheduleProposal"] = "scheduleProposal";
    return SubmitType;
}({});
export const GetAgreementStudioModalTID = ()=>getTestIds({
        deprecatedTestIds: {
            AgreementStudioModalTID: {
                SubmitButton: 'agreement-studio-topbar-btn-submit'
            }
        }
    });
function TemplateVariablesBridge(param) {
    let { onValuesChange, bridgeRef } = param;
    const { templateVariableValues, unresolvedCustomVariables, resolvedValues, setShowVariableErrors } = useTemplateVariables();
    useEffect(()=>{
        onValuesChange(templateVariableValues);
    }, [
        templateVariableValues,
        onValuesChange
    ]);
    useEffect(()=>{
        bridgeRef.current = {
            unresolvedCustomVariables,
            resolvedValues,
            setShowVariableErrors
        };
    }, [
        unresolvedCustomVariables,
        resolvedValues,
        setShowVariableErrors,
        bridgeRef
    ]);
    return null;
}
export default function AgreementStudioModal(props) {
    var _ref, _ref1, _ref2, _props_uiState;
    var _worksAt_settings, _worksAt_settings1, _worksAt_settings2, _worksAt_settings3, _draft_expirationDate, _worksAt_settings_expirationDate, _worksAt_settings4, _draft_expirationDate1, _worksAt_settings_expirationDate1, _worksAt_settings5, _worksAt_settings6, _worksAt_settings_feeControls, _worksAt_settings7, _worksAt_settings8;
    const { t: studioTexts } = useLocaleKeys('components.agreementStudio');
    const { t: controllerTexts } = useLocaleKeys('components.agreementControllers');
    const { t } = useLocaleKeys();
    const { isTemplateProposal, draft, legalTermsDocuments, worksAt, autoSaveTimeout = 1500, isSampleRelationship = false, agreementTitleMP, agreementTitle, savingStatusMP, modalTitle, modalTitleTestId, effectiveDate, additionalAutoSaveDependencies = [], inlineFooterActions, additionalFooterActions, contact, isReadOnly, legalTermsSourceId, getCustomPagesSourceId, onSubmitCb, onUnsavedChangesChange, onAutoSaveCb, openPreviewModal, renderSubmitButton } = props;
    useLegalTerms({
        refreshOnHookMount: true
    });
    const { isSendBlocked, setAutoSavePending, setCustomPageUploading } = useSendGate();
    const { AgreementStudioModalTID } = useMemo(()=>GetAgreementStudioModalTID(), []);
    const { isMobileDevice } = useDevice();
    const featureFlags = useFeatureFlags();
    const TEMPLATE_VARIABLES = featureFlags.TEMPLATE_VARIABLES;
    const enableCustomPages = customPagesEnabled(!!featureFlags.CUSTOM_PAGES, (_ref = draft === null || draft === void 0 ? void 0 : draft.customPages) !== null && _ref !== void 0 ? _ref : [], (_ref1 = draft === null || draft === void 0 ? void 0 : draft.introVideo) !== null && _ref1 !== void 0 ? _ref1 : null);
    const showIntroVideoField = !isMobileDevice && !enableCustomPages;
    const { logBI } = useStoreActions();
    const isMounted = useIsMounted();
    const { error: contactsError } = useContacts();
    const { disableScrollAnimation } = useAppConfig();
    const { browser: { scrollIntoView } } = useIoContext();
    const defaultIntroVideo = useStore(selectors.user.introVideo);
    const [wasSubmitted, setWasSubmitted] = useState(false);
    const includingExpenses = false;
    const isExistingCustomer = Boolean(draft === null || draft === void 0 ? void 0 : draft.isExistingCustomer);
    const coverLetterValid = true;
    const coverLetter = (_ref2 = draft === null || draft === void 0 ? void 0 : draft.coverLetter) !== null && _ref2 !== void 0 ? _ref2 : defaultCoverLetterNewClient;
    const [services, setServices] = useState(()=>draft ? draft.services : []);
    const [bundles, setBundles] = useState(()=>draft && draft.serviceBundles ? draft.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                services: b.services
            })) : []);
    const [agreementLegalTerms, setAgreementLegalTerms] = useState(()=>{
        const { legalTermsDocumentId } = worksAt.settings || {};
        if ((draft === null || draft === void 0 ? void 0 : draft.legalTermsDocuments) && draft.legalTermsDocuments.length > 0) {
            return draft.legalTermsDocuments;
        }
        const legalTermsDocument = legalTermsDocuments.find((param)=>{
            let { id } = param;
            return id === legalTermsDocumentId;
        });
        if (legalTermsDocument) {
            return [
                legalTermsModel.toRelationshipLegalTermsDocuments(legalTermsDocument)
            ];
        }
        return [];
    });
    const computeLegalTermErrors = useCallback((legalTerms)=>{
        return legalTerms.map((legalTerm)=>{
            var _legalTerm_termsOfService;
            const errors = {};
            if (legalTerm === null) {
                errors.legalTermField = t.pages.proposalDefaults.errors.pleaseChooseDocument();
            }
            if ((legalTerm === null || legalTerm === void 0 ? void 0 : (_legalTerm_termsOfService = legalTerm.termsOfService) === null || _legalTerm_termsOfService === void 0 ? void 0 : _legalTerm_termsOfService.type) === 'explicitApproval') {
                var _legalTerm_termsOfService_explicitApproval_statement, _legalTerm_termsOfService_explicitApproval_statement1;
                if (!((_legalTerm_termsOfService_explicitApproval_statement = legalTerm.termsOfService.explicitApproval.statement) === null || _legalTerm_termsOfService_explicitApproval_statement === void 0 ? void 0 : _legalTerm_termsOfService_explicitApproval_statement.trim())) {
                    errors.explicitApproval = studioTexts.legalTerms.errors.pleaseEnterCheckboxStatement();
                } else if (((_legalTerm_termsOfService_explicitApproval_statement1 = legalTerm.termsOfService.explicitApproval.statement) === null || _legalTerm_termsOfService_explicitApproval_statement1 === void 0 ? void 0 : _legalTerm_termsOfService_explicitApproval_statement1.length) > 120) {
                    errors.explicitApproval = studioTexts.legalTerms.errors.statementMaxLength();
                }
            }
            return errors;
        });
    }, []);
    // Check if there are validation errors - does NOT depend on wasSubmitted
    const hasLegalTermsErrors = useCallback(()=>{
        return computeLegalTermErrors(agreementLegalTerms).some((error)=>error.legalTermField || error.explicitApproval);
    }, [
        agreementLegalTerms,
        computeLegalTermErrors
    ]);
    // Errors to display in UI - only show after submit attempt
    const legalTermsErrors = useMemo(()=>{
        if (!wasSubmitted) {
            return [];
        }
        return computeLegalTermErrors(agreementLegalTerms);
    }, [
        wasSubmitted,
        agreementLegalTerms,
        computeLegalTermErrors
    ]);
    const [amendmentAutoApproval, setAmendmentAutoApproval] = useState((draft === null || draft === void 0 ? void 0 : draft.amendmentsApprovalMethod) ? approvalMethodNoticePeriodModel.isAutoApproval(draft.amendmentsApprovalMethod) : !!((_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod));
    const [amendmentAutoApprovalNoticePeriod, setAmendmentAutoApprovalNoticePeriod] = useState((draft === null || draft === void 0 ? void 0 : draft.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(draft.amendmentsApprovalMethod) ? draft.amendmentsApprovalMethod.auto.noticePeriodDays : ((_worksAt_settings1 = worksAt.settings) === null || _worksAt_settings1 === void 0 ? void 0 : _worksAt_settings1.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod) ? worksAt.settings.amendmentsApprovalMethod.auto.noticePeriodDays : 30);
    const [invoiceAutoApproval, setInvoiceAutoApproval] = useState((draft === null || draft === void 0 ? void 0 : draft.invoicesApprovalMethod) ? approvalMethodNoticePeriodModel.isAutoApproval(draft.invoicesApprovalMethod) : !!((_worksAt_settings2 = worksAt.settings) === null || _worksAt_settings2 === void 0 ? void 0 : _worksAt_settings2.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod));
    const [invoiceAutoApprovalNoticePeriod, setInvoiceAutoApprovalNoticePeriod] = useState((draft === null || draft === void 0 ? void 0 : draft.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(draft.invoicesApprovalMethod) ? draft.invoicesApprovalMethod.auto.noticePeriodDays : ((_worksAt_settings3 = worksAt.settings) === null || _worksAt_settings3 === void 0 ? void 0 : _worksAt_settings3.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod) ? worksAt.settings.invoicesApprovalMethod.auto.noticePeriodDays : 30);
    const [customPages, setCustomPages] = useState(()=>{
        var _ref;
        return (_ref = draft === null || draft === void 0 ? void 0 : draft.customPages) !== null && _ref !== void 0 ? _ref : [];
    });
    const [submittedCustomPageIds, setSubmittedCustomPageIds] = useState(()=>new Set());
    const customPagesRef = useRef(null);
    const [introVideo, setIntroVideo] = useState((draft === null || draft === void 0 ? void 0 : draft.introVideo) || (enableCustomPages ? makeIntroVideoNone() : defaultIntroVideo) || makeIntroVideoNone());
    const [daysToExpiration, setDaysToExpiration] = useState((draft === null || draft === void 0 ? void 0 : (_draft_expirationDate = draft.expirationDate) === null || _draft_expirationDate === void 0 ? void 0 : _draft_expirationDate.type) === 'days' ? draft.expirationDate.days : (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings4 = worksAt.settings) === null || _worksAt_settings4 === void 0 ? void 0 : (_worksAt_settings_expirationDate = _worksAt_settings4.expirationDate) === null || _worksAt_settings_expirationDate === void 0 ? void 0 : _worksAt_settings_expirationDate.type) === 'days' ? worksAt.settings.expirationDate.days : 30);
    const [hasExpirationDate, setHasExpirationDate] = useState(draft ? (draft === null || draft === void 0 ? void 0 : (_draft_expirationDate1 = draft.expirationDate) === null || _draft_expirationDate1 === void 0 ? void 0 : _draft_expirationDate1.type) === 'days' : (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings5 = worksAt.settings) === null || _worksAt_settings5 === void 0 ? void 0 : (_worksAt_settings_expirationDate1 = _worksAt_settings5.expirationDate) === null || _worksAt_settings_expirationDate1 === void 0 ? void 0 : _worksAt_settings_expirationDate1.type) === 'days');
    const [netTerms, setNetTerms] = useState((draft === null || draft === void 0 ? void 0 : draft.netTerms) != null ? draft === null || draft === void 0 ? void 0 : draft.netTerms : ((_worksAt_settings6 = worksAt.settings) === null || _worksAt_settings6 === void 0 ? void 0 : _worksAt_settings6.netTerms) || 0);
    const form = useForm({
        mode: 'all',
        defaultValues: {
            extraClause: (draft === null || draft === void 0 ? void 0 : draft.extraClause) || ''
        }
    });
    const [extraClause] = form.watch([
        'extraClause'
    ]);
    const [extraClauseValid, setExtraClauseValid] = useState(true);
    const [requirePaymentMethod, setRequirePaymentMethod] = useState(!isSampleRelationship && (draft === null || draft === void 0 ? void 0 : draft.requirePaymentMethod) !== false);
    const [allowManualPayments, setAllowManualPayments] = useState(!!(draft === null || draft === void 0 ? void 0 : draft.allowManualPayments));
    const [transactionFees, setTransactionFees] = useState(!!(draft ? draft.coverCreditCardFees : (_worksAt_settings7 = worksAt.settings) === null || _worksAt_settings7 === void 0 ? void 0 : (_worksAt_settings_feeControls = _worksAt_settings7.feeControls) === null || _worksAt_settings_feeControls === void 0 ? void 0 : _worksAt_settings_feeControls.coverCreditCardFees));
    let initialText;
    if (isExistingCustomer) {
        var _ref3;
        var _worksAt_settings9;
        initialText = (_ref3 = (_worksAt_settings9 = worksAt.settings) === null || _worksAt_settings9 === void 0 ? void 0 : _worksAt_settings9.introductoryMessages.invitation) !== null && _ref3 !== void 0 ? _ref3 : businessSettingsDefaults.introductoryMessages.invitation;
    } else {
        var _ref4;
        var _worksAt_settings10;
        initialText = (_ref4 = (_worksAt_settings10 = worksAt.settings) === null || _worksAt_settings10 === void 0 ? void 0 : _worksAt_settings10.introductoryMessages.proposal) !== null && _ref4 !== void 0 ? _ref4 : businessSettingsDefaults.introductoryMessages.proposal;
    }
    const [customNotificationText, setCustomNotificationText] = useState((draft === null || draft === void 0 ? void 0 : draft.customNotificationText) || initialText);
    const [showNoServicesWarning, setShowNoServicesWarning] = useState(false);
    const [scrollToServiceId, signalScrollToServiceId] = useSignal();
    const [showInvalidPackagesWarning, setShowInvalidPackagesWarning] = useState(false);
    const [showAgreementSettingsWarning, setShowAgreementSettingsWarning] = useState(false);
    const [showExtraClauseWarning, setShowExtraClauseWarning] = useState(false);
    const [showServiceValidationWarning, setShowServiceValidationWarning] = useState([]);
    const internalUiState = useStudioUiState();
    const { showAgreementSettings, setShowAgreementSettings, showPaymentSettings, setShowPaymentSettings, showTemplateVariablesSection, setShowTemplateVariablesSection } = (_props_uiState = props.uiState) !== null && _props_uiState !== void 0 ? _props_uiState : internalUiState;
    const { isInvalid } = useValidateService();
    // Auto-save stuff
    const autosaveTimerRef = useRef(null);
    const changesSavedTimerRef = useRef(null);
    const [needToSaveDraft, setNeedToSaveDraft] = useState(false);
    const servicePackagesRef = useRef(null);
    const [hasServiceError, setHasServiceError] = useState(false);
    const [templateVariableValues, setTemplateVariableValues] = useState(()=>{
        var _ref;
        return (_ref = draft === null || draft === void 0 ? void 0 : draft.templateVariableValues) !== null && _ref !== void 0 ? _ref : {};
    });
    const templateVariablesRef = useRef({
        unresolvedCustomVariables: [],
        resolvedValues: {},
        setShowVariableErrors: ()=>{}
    });
    // accountant access
    const [requestAccountantAccess, setRequestAccountantAccess] = useState(draft ? draft.requestAccountantAccess : (_worksAt_settings8 = worksAt.settings) === null || _worksAt_settings8 === void 0 ? void 0 : _worksAt_settings8.requestAccountantAccess);
    const hasRequestAccountantAccess = requestAccountantAccessModel.wasRequested(requestAccountantAccess);
    const agreementData = AgreementDataModel.make({
        netTerms,
        autoPayment: !allowManualPayments,
        status: RelationshipStatus.Pending,
        effectiveDate: effectiveDate && toDateStampObject(effectiveDate),
        daysToEffectiveDate: 0
    });
    const allServicesForVariableDetection = useMemo(()=>[
            ...services,
            ...bundles.flatMap((b)=>b.services)
        ], [
        services,
        bundles
    ]);
    // These fields must cover the same service fields resolved in resolveService (utils):
    // s.name, s.description, s.terms, s.bundle.services[i].name,
    // s.bundle.services[i].description, s.bundle.services[i].terms.
    // When adding a field to resolveService, add the corresponding entries here too.
    const templateVarTextFields = useMemo(()=>[
            agreementTitle !== null && agreementTitle !== void 0 ? agreementTitle : '',
            customNotificationText,
            ...allServicesForVariableDetection.map((s)=>s.name),
            ...allServicesForVariableDetection.flatMap((s)=>{
                var _ref;
                var _s_bundle;
                return (_ref = (_s_bundle = s.bundle) === null || _s_bundle === void 0 ? void 0 : _s_bundle.services.map((is)=>is.name)) !== null && _ref !== void 0 ? _ref : [];
            }),
            ...bundles.map((b)=>b.name)
        ], [
        agreementTitle,
        customNotificationText,
        allServicesForVariableDetection,
        bundles
    ]);
    const templateVarRichTextFields = useMemo(()=>[
            extraClause,
            ...allServicesForVariableDetection.map((s)=>s.description),
            ...allServicesForVariableDetection.map((s)=>s.terms),
            ...allServicesForVariableDetection.flatMap((s)=>{
                var _ref;
                var _s_bundle;
                return (_ref = (_s_bundle = s.bundle) === null || _s_bundle === void 0 ? void 0 : _s_bundle.services.map((is)=>is.description)) !== null && _ref !== void 0 ? _ref : [];
            }),
            ...allServicesForVariableDetection.flatMap((s)=>{
                var _ref;
                var _s_bundle;
                return (_ref = (_s_bundle = s.bundle) === null || _s_bundle === void 0 ? void 0 : _s_bundle.services.map((is)=>is.terms)) !== null && _ref !== void 0 ? _ref : [];
            })
        ], [
        extraClause,
        allServicesForVariableDetection
    ]);
    const onTemplateVariableValuesChange = useCallback((values)=>{
        setTemplateVariableValues((prev)=>{
            const prevKeys = Object.keys(prev);
            const nextKeys = Object.keys(values);
            if (prevKeys.length !== nextKeys.length) return values;
            for (const key of prevKeys){
                if (prev[key] !== values[key]) return values;
            }
            return prev;
        });
    }, []);
    // If any relevant fields changed, trigger an autosave
    useDidUpdate(()=>{
        onUnsavedChangesChange(true);
        if (isMounted() && onAutoSaveCb != null) {
            if (!autosaveTimerRef.current) {
                setAutoSavePending(true);
                const triggerSaveDraft = ()=>isMounted() && setNeedToSaveDraft(true);
                if (autoSaveTimeout === 0) {
                    triggerSaveDraft();
                } else {
                    autosaveTimerRef.current = setTimeout(triggerSaveDraft, autoSaveTimeout);
                }
            }
        }
    }, [
        allowManualPayments,
        amendmentAutoApproval,
        amendmentAutoApprovalNoticePeriod,
        invoiceAutoApproval,
        invoiceAutoApprovalNoticePeriod,
        autoSaveTimeout,
        bundles,
        coverLetter,
        customNotificationText,
        extraClause,
        includingExpenses,
        isSampleRelationship,
        netTerms,
        introVideo,
        requirePaymentMethod,
        services,
        customPages,
        transactionFees,
        requestAccountantAccess,
        daysToExpiration,
        hasExpirationDate,
        agreementLegalTerms,
        templateVariableValues,
        ...additionalAutoSaveDependencies
    ]);
    useEffect(()=>{
        if (needToSaveDraft && onAutoSaveCb != null && isMounted()) {
            setNeedToSaveDraft(false);
            setAutoSavePending(false);
            const data = makeAgreementStudioCommon();
            if (data != null) {
                autosaveTimerRef.current = null;
                onAutoSaveCb(data, isMounted);
                onUnsavedChangesChange(false);
            }
        } else {
            onUnsavedChangesChange(false);
        }
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        needToSaveDraft
    ]);
    useSetDocumentTitle(studioTexts.documentTitle());
    useEffect(()=>{
        // Cleanup timers.
        return ()=>{
            autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current);
            // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
            changesSavedTimerRef.current && clearTimeout(changesSavedTimerRef.current);
        };
    }, []);
    // Scroll to show the warnings in agreement settings
    useEffect(()=>{
        if (showAgreementSettingsWarning) {
            const errorMessageElement = document.querySelector('[data-testid="agreement-studio-customer-details-input-display-title-helper-text"]') || document.querySelector('[data-testid="pdf-file-not-uploaded-error"]') || document.querySelector('[data-testid="formling-extra-clause-input-helper-text"]');
            if (errorMessageElement) {
                setTimeout(()=>scrollIntoView(errorMessageElement, {
                        disableScrollAnimation: true
                    }), 100);
            }
        }
    }, [
        showAgreementSettingsWarning
    ]);
    function isServiceInvalid(s) {
        return isInvalid(s, {
            relationshipEffectiveDate: effectiveDate,
            isTemplate: isTemplateProposal
        });
    }
    const onOptionalChange = (serviceId, optional, includedByDefault)=>applyServiceChange(serviceId, (s)=>serviceModel.setParticipation(s, optional, includedByDefault));
    const onLimitBillingTimesChange = (serviceId, enabled, limit)=>applyServiceChange(serviceId, (s)=>serviceModel.setMaxCharges(s, enabled, limit));
    const onQboClassChange = (serviceId, integratedClass)=>applyServiceChange(serviceId, (s)=>serviceModel.setIntegratedClass(s, integratedClass));
    const onPausedChange = (serviceId, isPaused)=>applyServiceChange(serviceId, (s)=>serviceModel.setPaused(s, isPaused));
    const onBillOnAcceptanceChange = (serviceId, isBillOnAcceptance)=>applyServiceChange(serviceId, (s)=>serviceModel.setSkipBillingOnAcceptance(s, !isBillOnAcceptance));
    const onStartBillingAtChange = (serviceId, startBillingAt)=>applyServiceChange(serviceId, (s)=>serviceModel.setStartBillingAt(s, startBillingAt));
    const onProratedChange = (serviceId, isProrated)=>applyServiceChange(serviceId, (s)=>serviceModel.setProrated(s, isProrated));
    const onAutoPriceIncreaseChange = (serviceId, priceIncrease)=>applyServiceChange(serviceId, (s)=>serviceModel.setPriceIncrease(s, priceIncrease));
    function applyServiceChange(serviceId, transformer) {
        setBundles((prevBundles)=>prevBundles.length ? prevBundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map((s)=>s.id === serviceId ? transformer(s) : s)
                })) : prevBundles);
        setServices((prevServices)=>prevServices.length ? prevServices.map((s)=>s.id === serviceId ? transformer(s) : s) : prevServices);
    }
    if (contactsError) {
        throw makeSentryIgnoredError('Failed to fetch contacts list'); // Will trigger the react error-boundary
    }
    const makeAgreementStudioCommon = ()=>{
        const legalTermsDocuments = agreementLegalTerms.filter((legalTerm)=>legalTerm !== null);
        return {
            services,
            serviceBundles: {
                bundles
            },
            legalTermsDocuments,
            netTerms,
            coverLetter,
            extraClause,
            requirePaymentMethod,
            allowManualPayments,
            coverCreditCardFees: transactionFees,
            isExistingCustomer,
            customNotificationText: customNotificationText,
            amendmentsApprovalMethod: amendmentAutoApproval ? approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(amendmentAutoApprovalNoticePeriod || 30) : approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod(),
            invoicesApprovalMethod: invoiceAutoApproval ? approvalMethodNoticePeriodModel.makeAutoApprovalMethodNoticePeriod(invoiceAutoApprovalNoticePeriod || 30) : approvalMethodNoticePeriodModel.makeManualApprovalMethodNoticePeriod(),
            requestAccountantAccess,
            introVideo,
            expirationDate: makeExpirationDate(hasExpirationDate ? daysToExpiration : undefined),
            templateVariableValues: TEMPLATE_VARIABLES ? templateVariableValues : undefined,
            customPages
        };
    };
    function validateTemplateVariables() {
        if (TEMPLATE_VARIABLES && templateVariablesRef.current.unresolvedCustomVariables.length > 0) {
            setWasSubmitted(true);
            templateVariablesRef.current.setShowVariableErrors(true);
            setShowTemplateVariablesSection(true);
            setTimeout(()=>{
                const errorElement = document.querySelector('[aria-invalid="true"]');
                if (errorElement) {
                    scrollIntoView(errorElement, {
                        disableScrollAnimation
                    });
                }
            }, 100);
            return false;
        }
        return true;
    }
    function validateCustomPagesBeforeAction() {
        if (customPages.some(isCustomPageContentInvalid)) {
            setSubmittedCustomPageIds(new Set(customPages.map((p)=>p.id)));
            if (customPagesRef.current) {
                scrollIntoView(customPagesRef.current, {
                    disableScrollAnimation
                });
            }
            return false;
        }
        return true;
    }
    async function onSubmit(submitType) {
        setWasSubmitted(true);
        if (!services.length && !bundles.length || bundles.length && bundles.some((b)=>!b.services.length)) {
            // In case it was true before, this ensures we trigger the useEffect in ServicePackages that switches to the relevant package.
            setShowNoServicesWarning(false);
            setTimeout(()=>setShowNoServicesWarning(true));
            scrollRefIntoView(servicePackagesRef);
            return;
        }
        const invalidServices = services.filter(isServiceInvalid).concat(bundles.flatMap((bundle)=>bundle.services.filter(isServiceInvalid)));
        if (invalidServices.length > 0) {
            signalScrollToServiceId(invalidServices[0].id);
            setShowServiceValidationWarning(invalidServices.map(serviceModel.id));
            return;
        } else {
            setShowServiceValidationWarning([]);
        }
        // Check for yearly automatic billing errors
        const allServices = [
            ...services,
            ...bundles.flatMap((bundle)=>bundle.services)
        ];
        const yearlyAutomaticErrorService = allServices.find((service)=>hasYearlyAutomaticBillingError(service, featureFlags));
        if (yearlyAutomaticErrorService) {
            signalScrollToServiceId(yearlyAutomaticErrorService.id);
            return;
        }
        if (bundles.length === 1) {
            setShowInvalidPackagesWarning(true);
            scrollRefIntoView(servicePackagesRef);
            return;
        }
        if (!coverLetterValid || amendmentAutoApproval && !(amendmentAutoApprovalNoticePeriod && isAutoApprovalValid(amendmentAutoApprovalNoticePeriod))) {
            setShowAgreementSettings(true);
            setShowAgreementSettingsWarning(true);
            return;
        }
        const introMessageError = validateIntroMessage(customNotificationText);
        if (introMessageError) {
            const introMessageElement = document.querySelector('[data-testid="template-content"]');
            if (introMessageElement) {
                scrollIntoView(introMessageElement, {
                    disableScrollAnimation
                });
            }
            return;
        }
        if (!extraClauseValid) {
            setShowAgreementSettings(true);
            setShowExtraClauseWarning(true);
            return;
        }
        if (hasServiceError) {
            scrollRefIntoView(servicePackagesRef);
            return;
        }
        if (hasLegalTermsErrors()) {
            setShowAgreementSettings(true);
            const errorMessageElement = document.querySelector('[aria-invalid="true"]');
            if (errorMessageElement) {
                scrollIntoView(errorMessageElement, {
                    disableScrollAnimation
                });
            }
            return;
        }
        if (!validateTemplateVariables()) {
            return;
        }
        if (!validateCustomPagesBeforeAction()) return;
        const data = makeAgreementStudioCommon();
        if (data != null) {
            autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
            onSubmitCb(data, submitType);
        }
    }
    function scrollRefIntoView(ref) {
        setTimeout(()=>{
            ref.current && scrollIntoView(ref.current, {
                disableScrollAnimation
            });
        }, 100);
    }
    return /*#__PURE__*/ _jsxs(FormProvider, _object_spread_props(_object_spread({}, form), {
        children: [
            /*#__PURE__*/ _jsx(FullPageModalHeaderStart, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.modalHeader,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": modalTitleTestId,
                            children: modalTitle
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.statusContainer,
                            children: savingStatusMP
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.cardsContainer,
                children: /*#__PURE__*/ _jsxs(TemplateVariablesProvider, {
                    contact: contact !== null && contact !== void 0 ? contact : null,
                    worksAt: worksAt,
                    agreementName: agreementTitle !== null && agreementTitle !== void 0 ? agreementTitle : null,
                    effectiveDate: effectiveDate,
                    initialTemplateVariableValues: draft === null || draft === void 0 ? void 0 : draft.templateVariableValues,
                    textFields: templateVarTextFields,
                    richTextFields: templateVarRichTextFields,
                    children: [
                        TEMPLATE_VARIABLES && /*#__PURE__*/ _jsx(TemplateVariablesBridge, {
                            onValuesChange: onTemplateVariableValuesChange,
                            bridgeRef: templateVariablesRef
                        }),
                        /*#__PURE__*/ _jsx(FormlingContainer, {
                            title: studioTexts.agreementDetailsTitle(),
                            children: /*#__PURE__*/ _jsxs(Grid, {
                                align: "center",
                                rowGap: isMobileDevice ? 8 : 'small',
                                children: [
                                    agreementTitleMP,
                                    /*#__PURE__*/ _jsx(IntroMessage, {
                                        disabled: isReadOnly,
                                        onMessageChange: (newIntroMsg)=>setCustomNotificationText(newIntroMsg),
                                        initialTemplateContent: customNotificationText
                                    }),
                                    showIntroVideoField && /*#__PURE__*/ _jsx(IntroVideoField, {
                                        disabled: isReadOnly,
                                        onChange: setIntroVideo,
                                        introVideo: introVideo
                                    }),
                                    /*#__PURE__*/ _jsx(ExpirationDate, {
                                        disabled: isReadOnly,
                                        daysToExpiration: daysToExpiration,
                                        setDaysToExpiration: setDaysToExpiration,
                                        hasExpirationDate: hasExpirationDate,
                                        setHasExpirationDate: setHasExpirationDate
                                    })
                                ]
                            })
                        }),
                        enableCustomPages && /*#__PURE__*/ _jsx("div", {
                            ref: customPagesRef,
                            children: /*#__PURE__*/ _jsx(CustomPagesSection, {
                                customPages: customPages,
                                setCustomPages: setCustomPages,
                                isReadOnly: isReadOnly,
                                submittedPageIds: submittedCustomPageIds,
                                getSourceId: getCustomPagesSourceId,
                                onUploadingChange: setCustomPageUploading
                            })
                        }),
                        /*#__PURE__*/ _jsx(FormlingContainer, {
                            title: studioTexts.servicesProvidedTitle(),
                            children: /*#__PURE__*/ _jsx(ServicePackages, {
                                showErrors: wasSubmitted,
                                showEmptyWarning: showNoServicesWarning,
                                scrollToServiceId: scrollToServiceId,
                                ref: servicePackagesRef,
                                showInvalidPackagesWarning: showInvalidPackagesWarning,
                                services: services,
                                bundles: bundles,
                                isReadOnly: isReadOnly,
                                agreementData: agreementData,
                                showServiceValidationWarning: showServiceValidationWarning,
                                advancedSettingsProps: {
                                    relationshipState: {
                                        type: isTemplateProposal ? 'template' : 'awaitingApproval',
                                        effectiveDate: effectiveDate
                                    },
                                    onAutoPriceIncreaseChange,
                                    onPausedChange,
                                    onStartBillingAtChange,
                                    onBillOnAcceptanceChange,
                                    onProratedChange,
                                    onOptionalChange,
                                    onLimitBillingTimesChange,
                                    onQboClassChange,
                                    onFinancialCentsChange: (serviceId, financialCents, assignTeamMembers)=>applyServiceChange(serviceId, (s)=>serviceModel.setFinancialCentsIntegration(s, financialCents, assignTeamMembers)),
                                    onKarbonChange: (serviceId, karbon, assignTeamMembers)=>applyServiceChange(serviceId, (s)=>serviceModel.setKarbonIntegration(s, karbon, assignTeamMembers)),
                                    onLimitedTimeDiscountOfferChange: (serviceId, limitedTimeDiscountOffer)=>applyServiceChange(serviceId, (s)=>serviceModel.setLimitedTimeDiscountOffer(s, limitedTimeDiscountOffer)),
                                    onDiscountPeriodExpirationChange: (serviceId, discountPeriodExpiration)=>applyServiceChange(serviceId, (s)=>serviceModel.setDiscountPeriodExpiration(s, discountPeriodExpiration)),
                                    onPreapprovedAmountChange: (serviceId, amount, controlledByAdvancedSettings)=>applyServiceChange(serviceId, (s)=>serviceModel.setPreapprovedAmount(s, amount, controlledByAdvancedSettings)),
                                    onServiceChange: (updatedService)=>{
                                        applyServiceChange(updatedService.id, ()=>updatedService);
                                    }
                                },
                                isServiceInvalid: isServiceInvalid,
                                setBundles: setBundles,
                                setServices: setServices,
                                onHasError: setHasServiceError
                            })
                        }),
                        /*#__PURE__*/ _jsxs(FormlingContainer, {
                            title: studioTexts.paymentSettingsTitle(),
                            togglePanelView: true,
                            testId: "agreement-studio-payment-settings-panel",
                            isOpen: showPaymentSettings,
                            setIsOpen: setShowPaymentSettings,
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.marginBottom,
                                    children: /*#__PURE__*/ _jsx(NetTermsDropdown, {
                                        netTerms: netTerms,
                                        onChange: (value)=>{
                                            logBI(10470, {
                                                selectedNetTerms: value
                                            });
                                            setNetTerms(value);
                                        },
                                        isReadOnly: isReadOnly,
                                        testId: "agreement-studio-net-terms-dropdown"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(InnerSep, {
                                    thin: true
                                }),
                                /*#__PURE__*/ _jsx(CoverCreditCardFeesSelector, {
                                    vendorBusinessName: worksAt.name,
                                    clientBusinessName: contact === null || contact === void 0 ? void 0 : contact.companyName(),
                                    value: transactionFees,
                                    testId: "agreement-studio-transaction-fees",
                                    disabled: isReadOnly,
                                    onOpen: ()=>logBI(10482, {
                                            selected: transactionFees
                                        }),
                                    onChange: (selected)=>{
                                        logBI(10483, {
                                            selected
                                        });
                                        setTransactionFees(selected);
                                    }
                                }),
                                /*#__PURE__*/ _jsx(InnerSep, {
                                    thin: true
                                }),
                                /*#__PURE__*/ _jsx(WithInfoIcon, {
                                    info: /*#__PURE__*/ _jsx(Typography, {
                                        skin: "invertedStandard",
                                        children: studioTexts.paymentSettings.requirePaymentMethodInfo()
                                    }),
                                    children: /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                        condition: isSampleRelationship,
                                        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, {
                                                placement: "top",
                                                title: /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            skin: "invertedStandard",
                                                            weight: "bold",
                                                            children: controllerTexts.testingModeDisablesPayments()
                                                        }),
                                                        /*#__PURE__*/ _jsx(Typography, {
                                                            skin: "invertedStandard",
                                                            children: controllerTexts.testingModeDisablesPaymentsInfo()
                                                        })
                                                    ]
                                                }),
                                                children: /*#__PURE__*/ _jsx("div", {
                                                    children: children
                                                })
                                            }),
                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                            testId: "agreement-studio-require-payment-method-Checkbox",
                                            bi: {
                                                name: 'allow_approval_without_payment_method'
                                            },
                                            checked: !requirePaymentMethod && !isSampleRelationship,
                                            disabled: isSampleRelationship || isReadOnly,
                                            onChange: ()=>{
                                                logBI(requirePaymentMethod ? 10476 : 10475, {});
                                                setRequirePaymentMethod(!requirePaymentMethod);
                                            },
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                skin: isSampleRelationship ? 'disabled' : 'standard',
                                                children: controllerTexts.allowApproveWithoutPaymentMethod()
                                            })
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(InnerSep, {
                                    thin: true
                                }),
                                /*#__PURE__*/ _jsx(AllowManualPaymentsCheckbox, {
                                    isSampleRelationship: isSampleRelationship,
                                    isReadOnly: isReadOnly,
                                    value: allowManualPayments,
                                    onChange: setAllowManualPayments
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(FormlingContainer, {
                            title: studioTexts.agreementTermsTitle(),
                            togglePanelView: true,
                            isOpen: showAgreementSettings,
                            setIsOpen: setShowAgreementSettings,
                            testId: "agreement-studio-settings-panel",
                            children: [
                                /*#__PURE__*/ _jsx(LegalTerms, {
                                    withExplicitApproval: true,
                                    agreementLegalTerms: agreementLegalTerms,
                                    onAgreementLegalTermsChange: setAgreementLegalTerms,
                                    testId: AgreementStudioModalTID.LegalTerms(),
                                    isReadOnly: isReadOnly,
                                    errors: legalTermsErrors,
                                    editContext: "proposal",
                                    sourceId: legalTermsSourceId
                                }),
                                /*#__PURE__*/ _jsx(Separator, {}),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.checkboxWrapper,
                                    children: [
                                        /*#__PURE__*/ _jsx(ApprovalMethodNoticePeriodField, {
                                            info: studioTexts.amendmentsAutoApproveInfo(),
                                            text: studioTexts.autoApproveAmendments(),
                                            testId: "amendments-auto-approval",
                                            bi: {
                                                enabledCheckbox: {
                                                    name: 'amendments_auto_approval_enabled'
                                                },
                                                periodSelect: {
                                                    name: 'amendments_auto_approval_period'
                                                }
                                            },
                                            enabled: amendmentAutoApproval,
                                            onToggle: setAmendmentAutoApproval,
                                            isReadOnly: isReadOnly,
                                            noticePeriod: amendmentAutoApprovalNoticePeriod,
                                            onChangePeriod: setAmendmentAutoApprovalNoticePeriod
                                        }),
                                        /*#__PURE__*/ _jsx(ApprovalMethodNoticePeriodField, {
                                            info: studioTexts.invoicesAutoApproveInfo(),
                                            text: studioTexts.autoApproveOutOfScope(),
                                            testId: "invoices-auto-approval",
                                            bi: {
                                                enabledCheckbox: {
                                                    name: 'invoices_auto_approval_enabled'
                                                },
                                                periodSelect: {
                                                    name: 'invoices_auto_approval_period'
                                                }
                                            },
                                            enabled: invoiceAutoApproval,
                                            onToggle: setInvoiceAutoApproval,
                                            isReadOnly: isReadOnly,
                                            noticePeriod: invoiceAutoApprovalNoticePeriod,
                                            onChangePeriod: setInvoiceAutoApprovalNoticePeriod
                                        }),
                                        /*#__PURE__*/ _jsx(WithInfoIcon, {
                                            info: studioTexts.accountantAccessRequest.infoTooltip(),
                                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                                testId: "agreement-studio-accountant-access-checkbox",
                                                bi: {
                                                    name: 'request_accountant_access'
                                                },
                                                checked: hasRequestAccountantAccess,
                                                disabled: isReadOnly,
                                                onChange: ()=>{
                                                    const newVal = hasRequestAccountantAccess ? RequestAccountantAccess.None : RequestAccountantAccess.Mandatory;
                                                    setRequestAccountantAccess(newVal);
                                                },
                                                children: /*#__PURE__*/ _jsx(Typography, {
                                                    children: studioTexts.accountantAccessRequest.checkboxLabel()
                                                })
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(ExtraClause, {
                                    isReadOnly: isReadOnly,
                                    onValidation: setExtraClauseValid,
                                    showAgreementSettingsWarning: showExtraClauseWarning
                                })
                            ]
                        }),
                        TEMPLATE_VARIABLES && /*#__PURE__*/ _jsx(TemplateVariablesSection, {
                            wasSubmitted: wasSubmitted,
                            isOpen: showTemplateVariablesSection,
                            setIsOpen: setShowTemplateVariablesSection,
                            testId: "agreement-studio-template-variables"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(FullPageModalAdditionalActions, {
                inlineActions: compact(inlineFooterActions === null || inlineFooterActions === void 0 ? void 0 : inlineFooterActions({
                    validateCustomPages: validateCustomPagesBeforeAction
                })),
                dropdownActions: compact(additionalFooterActions)
            }),
            /*#__PURE__*/ _jsxs(FullPageModalMainActions, {
                children: [
                    /*#__PURE__*/ _jsx(Button, _object_spread_props(_object_spread({
                        bi: {
                            name: 'preview_agreement_studio_proposal'
                        },
                        skin: "primaryOutline",
                        testId: "agreement-studio-topbar-btn-preview"
                    }, isMobileDevice ? {
                        size: 'iconOnly'
                    } : {}), {
                        onClick: ()=>{
                            const data = makeAgreementStudioCommon();
                            if (data != null) {
                                if (TEMPLATE_VARIABLES) {
                                    const resolved = templateVariablesRef.current.resolvedValues;
                                    const resolvedTitle = agreementTitle ? resolveVariablesInText(agreementTitle, resolved) : undefined;
                                    openPreviewModal(_object_spread_props(_object_spread({}, data), {
                                        extraClause: resolveVariablesInDelta(data.extraClause, resolved),
                                        customNotificationText: resolveVariablesInText(data.customNotificationText, resolved),
                                        services: data.services.map((s)=>resolveService(s, resolved)),
                                        serviceBundles: data.serviceBundles ? {
                                            bundles: data.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                                                    name: resolveVariablesInText(b.name, resolved),
                                                    services: b.services.map((s)=>resolveService(s, resolved))
                                                }))
                                        } : undefined
                                    }), resolvedTitle);
                                } else {
                                    openPreviewModal(data);
                                }
                            }
                        },
                        children: isMobileDevice ? /*#__PURE__*/ _jsx(Eye, {
                            size: 16
                        }) : studioTexts.viewClientPreview()
                    })),
                    renderSubmitButton({
                        testId: AgreementStudioModalTID.SubmitButton(),
                        className: isMobileDevice ? '' : styles.submitBtn,
                        onSubmit,
                        validateTemplateVariables,
                        isSendBlocked
                    })
                ]
            })
        ]
    }));
}
